import { useEffect, useState } from 'react';
import { Controller, FieldError, useFormContext } from 'react-hook-form';
// Types
import { GetTicketVenuePlaceDetails_getPlaceDetails } from 'api/ticketEvents/types/GetTicketVenuePlaceDetails';
import { FormInputs } from 'components/CreatorSetup/StreamingSetup/CreateStreamSetup/CreateStreamSetup';
// Hooks
import { useEventVenueFields } from 'hooks/useEventVenueFields';
// Helpers
import {
  EVENT_VENUE_FIELDS,
  EventTicketTierDraft,
  EventVenueDraft,
  getVenueFromPlace,
} from 'helpers/ticketEvents';
// Ui
import Input from 'ui3/Input/Input';
import DatePicker from 'ui3/DatePicker/DatePicker';
import PlaceAutocomplete from 'ui3/PlaceAutocomplete/PlaceAutocomplete';
// Components
import TicketTiersEditor from 'components/ManageEvents/CreateEventSetup/PricingTicketsStep/TicketTiersEditor/TicketTiersEditor';
import SalesCloseSelect from 'components/ManageEvents/CreateEventSetup/SalesCloseSelect/SalesCloseSelect';
// Styles
import styles from './StreamTicketsFields.module.scss';

type VenueField = keyof EventVenueDraft;

const PLACE_FIELDS: VenueField[] = ['city', 'region', 'country', 'latitude'];
const TYPED_ADDRESS_FIELDS: {
  field: 'addressLine1' | 'postalCode';
  label: string;
  placeholder: string;
}[] = [
  {
    field: 'addressLine1',
    label: 'Street Address',
    placeholder: 'e.g. 4 Pennsylvania Plaza',
  },
  { field: 'postalCode', label: 'ZIP Code', placeholder: 'Zip' },
];

const StreamTicketsFields = (): JSX.Element => {
  const {
    register,
    errors,
    control,
    setValue,
    watch,
  } = useFormContext<FormInputs>();
  const [pickedVenue, setPickedVenue] = useState<EventVenueDraft | null>(null);
  const { venue, venueSearch, venueErrors } = useEventVenueFields();

  useEffect(() => {
    register({ name: 'tiers' });
  }, [register]);

  const tiers: EventTicketTierDraft[] = watch('tiers') || [];
  const addressError =
    venueErrors &&
    PLACE_FIELDS.map((field) => venueErrors[field]?.message).find(Boolean);
  // Some places come back without a street or ZIP code, so the host types those in.
  const isPlacePicked = venue.latitude !== null;
  const missingFields = isPlacePicked
    ? TYPED_ADDRESS_FIELDS.filter(({ field }) => !pickedVenue?.[field])
    : [];

  const setTiers = (next: EventTicketTierDraft[]) => {
    setValue('tiers', next, { shouldValidate: Boolean(errors?.tiers) });
  };

  // A name the host typed stays; the place only fills it in when it is empty.
  const handlePlaceSelect = (
    place: GetTicketVenuePlaceDetails_getPlaceDetails
  ) => {
    const nextVenue = getVenueFromPlace(place);
    setPickedVenue(nextVenue);

    EVENT_VENUE_FIELDS.forEach((field) => {
      if (field === 'name' && venue.name.trim()) {
        return;
      }

      setValue(`venue.${field}`, nextVenue[field], {
        shouldValidate: Boolean(venueErrors),
      });
    });
    setValue('venueSearch', place.formattedAddress);
  };

  return (
    <div className={styles.root}>
      <div className={styles.twoColumns}>
        <Input
          name="venue.name"
          label="Venue"
          placeholder="Venue Name"
          value={venue.name}
          onChange={(event) =>
            setValue('venue.name', event.target.value, {
              shouldValidate: Boolean(venueErrors?.name),
            })
          }
          error={venueErrors?.name?.message}
        />
        <Controller
          control={control}
          name="doorsOpenTime"
          render={({ onChange, onBlur, value }) => (
            <DatePicker
              selected={value || null}
              onBlur={onBlur}
              onChange={(time: Date | null) => onChange(time || '')}
              showTimeSelect
              showTimeSelectOnly
              dateFormat="hh:mm aa"
              placeholderText="--:-- AM/PM"
              customInput={
                <Input
                  label="Open Doors"
                  aria-label="Select the time doors open"
                  name="doorsOpenTimeInput"
                  endIcons={[{ name: 'clock' }]}
                  error={(errors?.doorsOpenTime as FieldError)?.message}
                />
              }
            />
          )}
        />
      </div>

      <Controller
        control={control}
        name="salesCloseHours"
        render={({ onChange, value }) => (
          <SalesCloseSelect value={value} onChange={onChange} />
        )}
      />

      <PlaceAutocomplete<GetTicketVenuePlaceDetails_getPlaceDetails>
        venue
        name="venueSearch"
        label="Address"
        placeholder="Venue Address"
        value={venueSearch || ''}
        onChange={handlePlaceSelect}
        error={addressError}
      />

      {missingFields.length > 0 && (
        <div className={styles.twoColumns}>
          {missingFields.map(({ field, label, placeholder }) => (
            <Input
              key={field}
              name={`venue.${field}`}
              label={label}
              placeholder={placeholder}
              value={venue[field]}
              onChange={(event) =>
                setValue(`venue.${field}`, event.target.value, {
                  shouldValidate: Boolean(venueErrors?.[field]),
                })
              }
              error={venueErrors?.[field]?.message}
            />
          ))}
        </div>
      )}

      <TicketTiersEditor
        tiers={tiers}
        onChange={setTiers}
        error={((errors?.tiers as unknown) as FieldError | undefined)?.message}
        emptyLabel="Ticket Tiers"
      />
    </div>
  );
};

export default StreamTicketsFields;
